/**
 * A tela "Hoje" (história 3.9) — a primeira tela depois do login.
 *
 *     /hoje    os 4 KPIs, a fila do dia e "Negócios que pedem ação" (as faixas saíram na 11.4)
 *
 * Ela é filha de `rotaDaAplicacao`, e por isso herda a guarda de sessão do layout — registrada
 * uma vez lá, ela vale para toda rota interna.
 *
 * **O caminho continua vindo de `CAMINHOS`.** O que mudou na 3.9 foi só quem desenha o corpo:
 * `/hoje` saiu do laço de placeholders de `telas.ts` (via `COM_TELA_PROPRIA`) e ganhou arquivo
 * próprio, como `/administracao` na 2.3, `/empresas` na 2.6 e `/funil` na 3.2. Item de menu e rota
 * continuam nascendo da **mesma** lista, e nem `inicio.tsx` (que manda `/` para cá) nem o destino
 * do login mudaram: `/hoje` já era a primeira tela — o que ela não era é uma tela.
 *
 * **A rota lê o catálogo e a régua; o painel lê o painel.** A divisão é a mesma de `funil.tsx`: o
 * que é **contexto da tela** (os Funis e as Etapas, os dois cortes da régua) a rota resolve e
 * passa por prop, e o que é o **conteúdo** daquele componente ele mesmo consulta. As duas leituras
 * daqui são compartilhadas por chave de cache com as telas vizinhas — o catálogo com o quadro e a
 * Administração, a régua com o cartão de Administração —, e por isso abrir `/hoje` depois de
 * `/funil` não custa ida à rede nenhuma delas.
 *
 * **Sem `?funil=` e sem parâmetro nenhum.** O painel atravessa **todos** os Funis que a policy
 * entrega: não há "onde eu estou" a guardar na URL, ao contrário do quadro.
 *
 * **E a rota não navega mais, e a ausência é da 3.10.** Até ali ela passava um `aoAbrirOFunil` que
 * levava a linha tocada ao quadro do Funil daquele Negócio — o único destino que existia. Com a
 * ficha (`/negocios/{id}`), cada linha é um `<Link>` para o próprio Negócio, e o componente o
 * escreve sozinho: um retorno de chamada aqui seria uma indireção que não decide nada.
 *
 * **Sem cabeçalho próprio.** O `Cabecalho` do shell já desenha o título ("Hoje") e o subtítulo do
 * item de navegação — a data de hoje por extenso —, e um segundo título aqui daria dois na mesma
 * tela. É a mesma regra que `/funil` e `/administracao` seguem.
 */

import { createRoute } from '@tanstack/react-router'

import { PainelDeHoje } from '@/modulos/negocios/PainelDeHoje'
import { useFunis, useRegua } from '@/modulos/negocios/api'
import { CAMINHOS } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/** Exportada: a rota `/negocios/$negocioId` a usa de fundo do modal (link colado, 11.4). */
export function TelaDeHoje() {
  // A **mesma** chave que o quadro e a Administração leem: uma consulta para as três telas.
  const catalogo = useFunis()
  const regua = useRegua()

  return (
    <PainelDeHoje
      funis={catalogo.data ?? []}
      regua={regua.data}
      /* A recusa do catálogo **não** derruba o painel — desde a 3.10 ela custa o **nome da Etapa**
         e mais nada: a linha leva à ficha, que não precisa do catálogo para abrir. Ela desce mesmo
         assim porque um nome que some sem explicação é indistinguível de uma Etapa que o serviço
         não conhece. */
      erroDoCatalogo={catalogo.isError ? catalogo.error : undefined}
      aoRecarregarOCatalogo={() => {
        void catalogo.refetch()
      }}
    />
  )
}

export const rotaDeHoje = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.hoje,
  component: TelaDeHoje,
})
